import MapView from "../map/MapView";
import {AppActions, useAppContext} from "../../context/AppContext";
import {v4} from "uuid";
import {useState, useRef, useCallback, useEffect, useLayoutEffect} from "react";
import {useMapSync} from "../../context/MapSyncContext";
import TileSelect from "../map-plugins/TileSelect";
import ProfileSelect from "../map-plugins/ProfileSelect";
import LayerSearch from "../map-plugins/LayerSearch";
import {Space} from "antd";
import '../../styles/side-by-side.css';

const getTiles = () => {
    return [
        {tile: 'Google Street', url: 'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', options: {subdomains:['mt0','mt1','mt2','mt3'], maxZoom: 25}, textColor: 'black', csColor: 'green', structure: '#808080'},
        {tile: 'Google Hybrid', url: 'http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', options: {subdomains:['mt0','mt1','mt2','mt3'], maxZoom: 25}, textColor: 'white', csColor: '#39FF14', structure: 'yellow'},
        {tile: 'Google Satellite', url: 'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', options: {subdomains:['mt0','mt1','mt2','mt3'], maxZoom: 25}, textColor: 'white', csColor: '#39FF14', structure: 'yellow'},
        {tile: 'Google Terrain', url: 'http://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}', options: {subdomains:['mt0','mt1','mt2','mt3'], maxZoom: 25}, textColor: 'black', csColor: 'green', structure: '#808080'},
        {tile: 'OpenStreetMap', url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', textColor: 'black', csColor: 'green', options: {maxZoom: 25}, structure: '#808080'},
        {tile: 'None', url: '', options: {maxZoom: 25}, textColor: 'black', csColor: 'green', structure: '#808080', isBlank: true},
    ]
}

export default function MapTab() {
    const {selectedHistoryId, getSelectedHistory, dispatchProject, getProjectLayers, getRiverBoundingCoords, getMapData, appContext, filter, getSelectedRS, resolveHistorySelection, selectedErrorId, selectedRS, getCSListByErrorId} = useAppContext()
    const { getSharedView } = useMapSync()
    const [sharedTile, setSharedTile] = useState(getTiles()[0])
    const [sliderPosition, setSliderPosition] = useState(50)
    const [isDragging, setIsDragging] = useState(false)
    const [profile, setProfile] = useState(null)
    const baseMapRef = useRef(null)
    const compareMapRef = useRef(null)
    const containerRef = useRef(null)
    
    // Check if compare data exists
    const hasCompareData = !!getMapData('compare')

    // If no compare data, always use 'base' mode
    const type = hasCompareData ? (appContext?.selectedMapType || 'base') : 'base'
    const isSideBySide = type === 'sidebyside'

    // Switching from Message to Map: make sure a river station from the current
    // message is selected (defaulting to the first) so the map highlights and
    // centers on it. Mirrors PlotTab so the behaviour is consistent across tabs
    // and for every message code. Runs on mount (tab switch) and when the
    // message (error id) changes; a still-valid selection is preserved.
    useEffect(() => {
        const list = getCSListByErrorId()
        if (selectedErrorId && list?.length && !list.find(x => x === selectedRS)) {
            dispatchProject({
                type: AppActions.SET_SELECTED_RS,
                data: {rs: list[0]}
            })
        }
    }, [selectedErrorId]);

    const getProjectInfo = (mapType) => {
        const resolved = resolveHistorySelection(getSelectedHistory())
        const selection = resolved?.[mapType]
        return {
            title: `${selection?.project?.information?.title} (${selection?.project?.information?.folderName})`,
            plan: `${selection?.plan?.type} (.${selection?.plan?.plan?.ext})`,
            river: selection?.river?.river ? `${selection?.river?.river} - ${selection?.river?.reach}` : '',
        }
    }

    const handleTypeChange = (newType) => {
        if ((newType === 'compare' || newType === 'sidebyside') && !hasCompareData) return
        dispatchProject({
            type: AppActions.SET_MAP_TYPE,
            data: { mapType: newType }
        })
    }

    const createProjectLayerChangeHandler = (mapType) => (shapes) => {
        const ref = getSelectedHistory()?.selection?.[mapType]
        const projectId = ref?.projectId || ref?.project?.id || ref?.project?.information?.id
        const _project = appContext?.projects?.find(x => x.id === projectId)
        if (_project) {
            _project.shapes = [...shapes]
            dispatchProject({
                type: AppActions.SET_PROJECT,
                data: {
                    project: {
                        ..._project
                    },
                    map: {
                        type: mapType,
                        [mapType]: {
                            update: v4()
                        }
                    }
                }
            })
        }
    }

    // Sync maps for side-by-side mode
    const isSyncing = useRef(false)
    const syncMaps = useCallback((sourceMap, targetMap) => {
        if (isSyncing.current || !sourceMap || !targetMap) return
        isSyncing.current = true
        const center = sourceMap.getCenter()
        const zoom = sourceMap.getZoom()
        targetMap.setView(center, zoom, { animate: false })
        setTimeout(() => { isSyncing.current = false }, 10)
    }, [])

    // Setup sync when entering sidebyside mode
    useEffect(() => {
        if (type !== 'sidebyside') return
        
        const baseMap = baseMapRef.current
        const compareMap = compareMapRef.current
        if (!baseMap || !compareMap) return

        const onBaseMove = () => syncMaps(baseMap, compareMap)
        const onCompareMove = () => syncMaps(compareMap, baseMap)

        baseMap.on('move', onBaseMove)
        baseMap.on('zoom', onBaseMove)
        compareMap.on('move', onCompareMove)
        compareMap.on('zoom', onCompareMove)

        // Initial sync from sharedView
        setTimeout(() => {
            const sharedView = getSharedView()
            baseMap.invalidateSize()
            compareMap.invalidateSize()
            if (sharedView?.zoom && sharedView?.zoom > 0 && sharedView?.center) {
                isSyncing.current = true
                baseMap.setView(sharedView.center, sharedView.zoom, { animate: false })
                compareMap.setView(sharedView.center, sharedView.zoom, { animate: false })
                setTimeout(() => { isSyncing.current = false }, 10)
            }
        }, 100)

        return () => {
            baseMap.off('move', onBaseMove)
            baseMap.off('zoom', onBaseMove)
            compareMap.off('move', onCompareMove)
            compareMap.off('zoom', onCompareMove)
        }
    }, [type, syncMaps, getSharedView])

    // Slider drag handlers
    const handleMouseDown = (e) => {
        setIsDragging(true)
        e.preventDefault()
    }

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100))
        setSliderPosition(percentage)
    }, [isDragging])

    const handleMouseUp = useCallback(() => {
        setIsDragging(false)
    }, [])

    const handleTouchMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return
        const touch = e.touches[0]
        const rect = containerRef.current.getBoundingClientRect()
        const x = touch.clientX - rect.left
        const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100))
        setSliderPosition(percentage)
    }, [isDragging])

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.addEventListener('touchmove', handleTouchMove)
            document.addEventListener('touchend', handleMouseUp)
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.removeEventListener('touchmove', handleTouchMove)
            document.removeEventListener('touchend', handleMouseUp)
        }
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove])

    // Layer search handler
    const handleLayerSearch = useCallback((item) => {
        if (!item) return
        if (item.type === 'cross_section') {
            dispatchProject({
                type: AppActions.SET_SELECTED_RS,
                data: {rs: item._text}
            })
        }
    }, [dispatchProject])

    // Toolbar for sidebyside mode
    useEffect(() => {
        if (isSideBySide) {
            dispatchProject({
                type: AppActions.SET_MESSAGE_TOOLBAR_ITEM,
                data: {
                    toolbar: (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <TileSelect value={sharedTile} onChange={setSharedTile} tiles={getTiles()}/>
                            <ProfileSelect profiles={getMapData('base')?.profiles} onChangeProfile={setProfile} selectedProfile={profile} />
                            <Space.Compact size={'small'}>
                                <LayerSearch onChange={handleLayerSearch} type="base"/>
                            </Space.Compact>
                        </div>
                    )
                }
            })
            return () => {
                dispatchProject({
                    type: AppActions.SET_MESSAGE_TOOLBAR_ITEM,
                    data: {toolbar: null}
                })
            }
        }
    }, [isSideBySide, sharedTile, profile, dispatchProject, handleLayerSearch, getMapData])

    // Genislik CSS %100 ile ebeveyne tam oturur (yatay scroll yok). Yukseklik, harita
    // alaninin gercek ust offset'inden olculur -> viewport'u tam doldurur, dikey scroll
    // yok. (Eski sabit innerHeight-125 / innerWidth-2 gercek header/margin ile tutmadigi
    // icin birkac px tasiyordu.)
    const [containerHeight, setContainerHeight] = useState(window.innerHeight - 125)
    useLayoutEffect(() => {
        const measure = () => {
            const el = containerRef.current
            if (!el) return
            const top = el.getBoundingClientRect().top
            const next = Math.max(0, Math.floor(window.innerHeight - top))
            // No-op when unchanged so the ResizeObserver below can't loop.
            setContainerHeight(prev => (prev === next ? prev : next))
        }
        measure()
        // On first mount (e.g. arriving via "Go to map") the chrome above the map
        // can still be settling, so the initial getBoundingClientRect().top is a few
        // px short -> the container ends up slightly too tall -> a phantom scrollbar
        // until the next relayout. Re-measure once the layout has painted, and again
        // whenever the surrounding page chrome changes size.
        const raf = requestAnimationFrame(measure)
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
        ro?.observe(document.body)
        window.addEventListener('resize', measure)
        return () => {
            cancelAnimationFrame(raf)
            ro?.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    // On the Map tab the map fills the entire viewport, so the page itself never
    // needs to scroll. Lock the root scrollbar off while this tab is mounted: the
    // map toolbar (LayerSearch/TileSelect) is injected a frame AFTER mount and
    // shifts the layout by a few px; because React applies the re-measured height
    // one render later, that frame would otherwise flash a phantom scrollbar. The
    // re-measure above still settles the exact height; this just hides the
    // transient overshoot. Scoped to the Map tab — other pages keep normal scroll.
    useLayoutEffect(() => {
        const root = document.documentElement
        const prev = root.style.overflow
        root.style.overflow = 'hidden'
        return () => { root.style.overflow = prev }
    }, [])
    const size = { height: containerHeight, width: '100%' }

    // Calculate styles based on mode
    const getBaseStyle = () => {
        if (type === 'base') return { display: 'block', width: '100%', height: '100%' }
        if (type === 'compare') return { display: 'none' }
        // sidebyside
        return {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
            zIndex: 1
        }
    }

    const getCompareStyle = () => {
        if (type === 'compare') return { display: 'block', width: '100%', height: '100%' }
        if (type === 'base') return { display: 'none' }
        // sidebyside
        return {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            clipPath: `inset(0 0 0 ${sliderPosition}%)`,
            zIndex: 2
        }
    }

    return (
        <div 
            ref={containerRef}
            className={isSideBySide ? 'sbs-container' : ''}
            style={{ 
                position: 'relative', 
                width: size.width, 
                height: size.height,
                overflow: 'hidden'
            }}
        >
            {/* Base map */}
            <div style={getBaseStyle()}>
                <MapView
                    key="map-base-persistent"
                    type="base"
                    currentMapType={isSideBySide ? 'sidebyside' : type}
                    projection={(resolveHistorySelection(getSelectedHistory())?.base?.project)?.projection?.text}
                    filter={filter}
                    projectInfo={getProjectInfo('base')}
                    updateKey={selectedHistoryId}
                    mapData={getMapData('base')}
                    projectLayers={getProjectLayers('base')}
                    riverBoundingCoords={getRiverBoundingCoords('base')}
                    selectedRS={getSelectedRS()}
                    onTypeChange={handleTypeChange}
                    hasCompareData={hasCompareData}
                    onProjectLayerChange={createProjectLayerChangeHandler('base')}
                    isVisible={type === 'base' || isSideBySide}
                    isSideBySide={isSideBySide}
                    sharedTile={sharedTile}
                    onSharedTileChange={setSharedTile}
                    onMapReady={(map) => { baseMapRef.current = map }}
                />
            </div>

            {/* Compare map */}
            {hasCompareData && (
                <div style={getCompareStyle()}>
                    <MapView
                        key="map-compare-persistent"
                        type="compare"
                        currentMapType={isSideBySide ? 'sidebyside' : type}
                        controlPosition={isSideBySide ? 'topright' : 'topleft'}
                        projection={(resolveHistorySelection(getSelectedHistory())?.compare?.project)?.projection?.text}
                        filter={filter}
                        projectInfo={getProjectInfo('compare')}
                        updateKey={selectedHistoryId}
                        mapData={getMapData('compare')}
                        projectLayers={getProjectLayers('compare')}
                        riverBoundingCoords={getRiverBoundingCoords('compare')}
                        selectedRS={getSelectedRS()}
                        onTypeChange={handleTypeChange}
                        hasCompareData={hasCompareData}
                        onProjectLayerChange={createProjectLayerChangeHandler('compare')}
                        isVisible={type === 'compare' || isSideBySide}
                        isSideBySide={isSideBySide}
                        sharedTile={sharedTile}
                        onSharedTileChange={setSharedTile}
                        onMapReady={(map) => { compareMapRef.current = map }}
                    />
                </div>
            )}

            {/* Slider - only in sidebyside mode */}
            {isSideBySide && hasCompareData && (
                <div 
                    className="sbs-slider"
                    style={{ 
                        left: `${sliderPosition}%`,
                        zIndex: 1000
                    }}
                >
                    <div className="sbs-slider-line" />
                    <div 
                        className="sbs-slider-handle"
                        onMouseDown={handleMouseDown}
                        onTouchStart={() => setIsDragging(true)}
                    />
                </div>
            )}
        </div>
    )
}